/* @flow */

import * as React from 'react';
import { Redirect } from 'react-router-dom';

import { allRoles } from 'permissions/general';
import { createNewProject, updateEditProject } from 'permissions/project';
import ProjectDetailsPage from 'views/ProjectDetailsPage/ProjectDetailsPage';
import ProjectsReportView from 'tables/ProjectsReportView/ProjectsReportView';
import { timeRangeToString } from 'stores/ReportViewStore';
import { formulateUri } from 'tables/components/ReportView/state';

import ProjectForm from '../forms/ProjectForm/ProjectForm';
import type { TypeComponent } from './types.js';
import { filterTradingAccountItem } from './utils/filters';

const projectRoutes: Array<*> = [
  // Return a list of all projects
  {
    path: '/projects',
    title: 'general:document_titles.projects.title',
    component: ProjectsReportView,
    exact: true,
    permissions: allRoles,
  },
  // Create a new project
  {
    path: '/projects/create',
    title: 'general:actions.new',
    component(): React.Element<*> {
      return <ProjectForm mode="create" />;
    },
    exact: true,
    permissions: createNewProject,
  },
  // Edit a project
  {
    path: '/projects/edit',
    title: 'general:document_titles.projects.edit',
    title_keys: ['details.name'],
    component({ location }) {
      return <ProjectForm mode="edit" projectData={location.state ? location.state : {}} />;
    },
    exact: true,
    permissions: updateEditProject,
  },
  {
    path: '/projects/:id',
    title: 'general:document_titles.projects.detail',
    title_keys: ['details.name'],
    component: ProjectDetailsPage,
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/projects/:id/:actionType',
    title: 'general:document_titles.projects.action_type',
    title_keys: ['details.name', 'currentActionDrawer'],
    component: ProjectDetailsPage,
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/projects/edit/:projectId',
    title: 'general:document_titles.projects.edit',
    component(props: Object): React.Element<*> {
      return <ProjectForm mode="edit" projectId={props.match.params.projectId} />;
    },
    exact: true,
    permissions: updateEditProject,
  },
  {
    path: '/projects/account/:accountId',
    component: function ProjectsByAccount(props: *) {
      return (
        <Redirect
          to={formulateUri('/projects', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/projects/account/:accountId/status/:status',
    component: function ProjectsByAccount(props: *) {
      return (
        <Redirect
          to={formulateUri('/projects', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'status', values: [props.match.params.status] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/projects/status/:status',
    component: function ProjectsByAccount(props: *) {
      return (
        <Redirect
          to={formulateUri('/projects', {
            page: 1,
            filters: [{ key: 'status', values: [props.match.params.status] }],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/projects/status/:status/:time',
    component: function ProjectsByAccount(props: *): TypeComponent {
      const projectTime = props.match.params.time;
      const today = new Date();
      const firstDay = new Date();
      const week = new Date(firstDay.getTime() + 7 * 24 * 60 * 60 * 1000);
      const month = new Date(firstDay.getTime() + 30 * 24 * 60 * 60 * 1000);

      const filters = [{ key: 'status', values: [props.match.params.status] }];

      if (projectTime === 'Week') {
        filters.push({ key: 'start_date', values: [timeRangeToString({ from: today, to: week })] });
      } else if (projectTime === 'Month') {
        filters.push({
          key: 'start_date',
          values: [timeRangeToString({ from: today, to: month })],
        });
      }

      return (
        <Redirect
          to={formulateUri('/projects', {
            page: 1,
            filters,
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/projects/cost-center/:costCenterId',
    component: function ProjectsByCostCenter(props: *): TypeComponent {
      const costCenterParam: string = encodeURIComponent(
        `cost_centre:${props.match.params.costCenterId}`
      );
      const url: string = `projects?search=${costCenterParam}`;
      return (
        <Redirect
          to={formulateUri('/projects', {
            page: 1,
            filters: [{ key: 'cost_centre', values: [props.match.params.costCenterId] }],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/projects/account/:accountId/user/:userId',
    component: function ProjectsByAccountAndUser(props: *): TypeComponent {
      return (
        <Redirect
          to={formulateUri('/projects', {
            page: 1,
            filters: [
              ...filterTradingAccountItem({
                key: 'account',
                values: [props.match.params.accountId],
              }),
              { key: 'engineer', values: [props.match.params.userId] },
            ],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
  {
    path: '/projects/location/:locationId',
    component: function AssetsByLocation(props: *): TypeComponent {
      return (
        <Redirect
          to={formulateUri('/projects', {
            page: 1,
            filters: [{ key: 'location', values: [props.match.params.locationId] }],
          })}
        />
      );
    },
    exact: true,
    permissions: allRoles,
  },
];

export default projectRoutes.map(item => ({
  ...item,
  store: ['projectDetailsStore', 'actionDrawerStore'],
}));
